import type { SafetyWindow } from '@magicschool/business-logic';
import type { Tables } from '@magicschool/supabase/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Room } from '@/app/api/rooms/types';
import { getNextSafetyWindowTransition } from '../../_helpers/safetyWindow';

interface UseSchedulingTimerOptions {
  /** How often to check for updates in milliseconds (default: 60000ms / 1 minute) */
  interval?: number;
  /** Array of rooms to monitor for scheduling transitions */
  rooms?: Room[];
  /** Single room to monitor for scheduling transitions */
  room?: Partial<Tables<'rooms'>> | null;
  /** Recurring windows (the room's own hours, the org's) whose next open/close flip should also wake the timer. */
  windows?: (SafetyWindow | null)[];
}

/**
 * Shared timer hook for scheduling status updates.
 *
 * OPTIMIZATION STRATEGY:
 * Instead of polling every minute regardless of schedule state, this hook uses
 * "smart timing" that only updates when schedule transitions are actually possible.
 *
 * Benefits:
 * - Reduces unnecessary CPU usage when no schedule changes are pending
 * - Uses precise timeouts for immediate updates at transition moments
 * - Automatically stops timers when no scheduled rooms are present
 * - Prevents memory leaks with proper cleanup
 */
export const useSchedulingTimer = ({ interval = 60_000, rooms, room, windows }: UseSchedulingTimerOptions) => {
  const [now, setNow] = useState(() => new Date());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Calculates the next moment when any room's scheduling status will change.
   * This allows us to set precise timeouts instead of continuous polling.
   */
  const getNextTransitionTime = useCallback(() => {
    const currentTime = new Date();
    let nextTransition: Date | undefined;

    // Support both single room and multiple rooms patterns
    const roomsToCheck = rooms || (room ? [room] : []);

    for (const r of roomsToCheck) {
      // Skip rooms without scheduling enabled
      if (!r?.scheduling_enabled) continue;

      const startTime = r.scheduled_start_at ? new Date(r.scheduled_start_at) : null;
      const endTime = r.scheduled_end_at ? new Date(r.scheduled_end_at) : null;

      // Check if room has a future start time (not-started -> active transition)
      if (startTime && startTime > currentTime) {
        if (!nextTransition || startTime < nextTransition) {
          nextTransition = startTime;
        }
      }

      // Check if room has a future end time (active -> ended transition)
      if (endTime && endTime > currentTime) {
        if (!nextTransition || endTime < nextTransition) {
          nextTransition = endTime;
        }
      }
    }

    for (const window of windows ?? []) {
      const flip = window && getNextSafetyWindowTransition(currentTime, window);
      if (flip && (!nextTransition || flip < nextTransition)) nextTransition = flip;
    }

    return nextTransition;
  }, [rooms, room, windows]);

  useEffect(() => {
    // Early exit: No scheduling enabled or invalid interval
    const hasScheduledItems = rooms?.some((r) => r.scheduling_enabled) || room?.scheduling_enabled || windows?.some(Boolean);

    if (!hasScheduledItems || interval <= 0) {
      // Clean up any existing timer when no scheduling is active
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Calculate when the next scheduling transition will occur
    const nextTransition = getNextTransitionTime();
    if (!nextTransition) {
      // No future transitions - all schedules have ended, stop timer
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Use `now` (the state value) to ensure this effect re-runs after each transition.
    // This is critical: when a timeout fires and updates `now`, this effect must re-run
    // to schedule the next transition timer (e.g., after start time, schedule end time).
    const timeUntilTransition = nextTransition.getTime() - now.getTime();

    // SMART TIMING OPTIMIZATION:
    // If transition is soon (within one interval), use precise timeout
    // instead of interval to update exactly when status changes
    if (timeUntilTransition <= interval) {
      const timeoutId = setTimeout(() => {
        setNow(new Date());
      }, timeUntilTransition + 1000); // +1s buffer to ensure transition has occurred

      return () => clearTimeout(timeoutId);
    }

    // Transition is far away - use regular interval polling
    // but first clean up any existing timer to avoid duplicates
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setNow(new Date());
    }, interval);

    // Cleanup function for component unmount or dependency changes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [rooms, room?.scheduling_enabled, interval, getNextTransitionTime, now, windows]);

  // Return current time for consuming components to use in their calculations
  return now;
};
